import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { formatCurrency } from '../../currencyUtil';

const STATUS = {
  pending:  { cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-800',       label: 'Pending'  },
  accepted: { cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-800', label: 'Accepted' },
  rejected: { cls: 'bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800',                   label: 'Declined' },
};

const DEAL_STATUS = {
  draft:    { color: 'text-amber-600 dark:text-amber-400',     dot: 'bg-amber-400',   label: 'Draft' },
  sent:     { color: 'text-blue-600 dark:text-blue-400',       dot: 'bg-blue-500',    label: 'Sent' },
  complete: { color: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Complete' },
  archived: { color: 'text-slate-500 dark:text-slate-400',     dot: 'bg-slate-400',   label: 'Archived' },
};

function carLabel(bid) {
  return [bid.year, bid.make, bid.model, bid.trim].filter(Boolean).join(' ') || bid.vin || `Car #${bid.car_id}`;
}

export default function BidsTab({ onPendingChange }) {
  const [bids, setBids]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('all');
  const [counterAmounts, setCounterAmounts] = useState({});
  const [responding, setResponding]         = useState({});
  const [actionErrors, setActionErrors]     = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get('/bids?mine=true')
      .then(d  => { if (mounted) setBids(d); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const respondToCounter = async (bid, status) => {
    setResponding(r => ({ ...r, [bid.id]: true }));
    setActionErrors(e => ({ ...e, [bid.id]: null }));
    try {
      const updated = await api.put(`/bids/${bid.id}`, { status });
      setBids(prev => prev.map(b => b.id === bid.id ? { ...b, ...updated } : b));
    } catch (err) {
      setActionErrors(e => ({ ...e, [bid.id]: err.message }));
    } finally {
      setResponding(r => ({ ...r, [bid.id]: false }));
    }
  };

  const submitCounter = async (bid) => {
    const price = counterAmounts[bid.id];
    if (!price) return;
    setResponding(r => ({ ...r, [bid.id]: true }));
    setActionErrors(e => ({ ...e, [bid.id]: null }));
    try {
      const updated = await api.post(`/bids/${bid.id}/counter`, { price });
      setBids(prev => prev.map(b => b.id === bid.id ? { ...b, ...updated } : b));
      setCounterAmounts(a => ({ ...a, [bid.id]: '' }));
    } catch (err) {
      setActionErrors(e => ({ ...e, [bid.id]: err.message }));
    } finally {
      setResponding(r => ({ ...r, [bid.id]: false }));
    }
  };

  // Awaiting the buyer's response: seller countered a still-pending bid.
  const awaitingMe = bids.filter(b => b.status === 'pending' && b.last_offer_by === 'seller').length;
  useEffect(() => { onPendingChange?.(awaitingMe); }, [awaitingMe]); // eslint-disable-line

  const visible = filter === 'all' ? bids : bids.filter(b => b.status === filter);

  const dealMap = new Map();
  for (const bid of visible) {
    if (!dealMap.has(bid.deal_id)) dealMap.set(bid.deal_id, { deal: bid, bids: [] });
    dealMap.get(bid.deal_id).bids.push(bid);
  }
  for (const { bids: dealBids } of dealMap.values()) {
    dealBids.sort((a, b) => {
      const aWaiting = a.status === 'pending' && a.last_offer_by === 'seller';
      const bWaiting = b.status === 'pending' && b.last_offer_by === 'seller';
      if (aWaiting !== bWaiting) return aWaiting ? -1 : 1;
      return Number(b.bid_price) - Number(a.bid_price);
    });
  }
  const dealGroups = [...dealMap.values()].map(({ deal, bids: dealBids }) => {
    const waitingCount = dealBids.filter(b => b.status === 'pending' && b.last_offer_by === 'seller').length;
    return { deal, bids: dealBids, waitingCount };
  }).sort((a, b) => b.waitingCount - a.waitingCount || a.deal.deal_id - b.deal.deal_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{error}</div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          {awaitingMe > 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
              {awaitingMe} counter-offer{awaitingMe !== 1 ? 's' : ''} awaiting your response
            </p>
          )}
        </div>
        {bids.length > 0 && (
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {['all', 'pending', 'accepted', 'rejected'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${
                  filter === f
                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}>
                {f}
              </button>
            ))}
          </div>
        )}
      </div>

      {!bids.length ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">No bids yet</h3>
          <p className="text-sm text-slate-400 dark:text-slate-500">Bids you place on available deals will appear here.</p>
        </div>
      ) : dealGroups.length === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-500 py-12 text-center">No {filter} bids.</p>
      ) : (
        <div className="space-y-6">
          {dealGroups.map(({ deal, bids: dealBids, waitingCount }) => {
            const ds = DEAL_STATUS[deal.deal_status] || DEAL_STATUS.sent;
            return (
              <div key={deal.deal_id} className="card overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => navigate(`/buyer/deals/${deal.deal_uuid}`)}
                    className="font-semibold text-slate-800 dark:text-slate-100 hover:underline truncate min-w-0"
                  >
                    {deal.deal_name || `Deal #${deal.deal_id}`}
                  </button>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium shrink-0 ${ds.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${ds.dot} shrink-0`} />
                    {ds.label}
                  </span>
                  <div className="flex-1" />
                  {waitingCount > 0 && (
                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 shrink-0">
                      {waitingCount} awaiting you
                    </span>
                  )}
                </div>

                <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {dealBids.map(bid => {
                    const s = STATUS[bid.status] || STATUS.pending;
                    const awaitingSeller  = bid.status === 'pending' && bid.last_offer_by === 'buyer';
                    const sellerCountered = bid.status === 'pending' && bid.last_offer_by === 'seller';
                    const history = bid.offer_history || [];
                    return (
                      <div key={bid.id} className="flex flex-wrap sm:flex-nowrap items-start gap-4 px-5 py-3">
                        <div className="basis-full sm:basis-auto flex-1 min-w-0 pt-0.5">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{carLabel(bid)}</p>
                          {history.length > 1 && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 truncate">
                              {history.map((o, i) => (
                                <React.Fragment key={i}>
                                  {i > 0 && ' → '}
                                  <span className={o.offered_by === 'seller' ? 'text-blue-500 dark:text-blue-400 font-medium' : ''}>
                                    ${formatCurrency(o.price)}
                                  </span>
                                </React.Fragment>
                              ))}
                            </p>
                          )}
                          {actionErrors[bid.id] && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{actionErrors[bid.id]}</p>
                          )}
                        </div>

                        {bid.bid_type === 'buy_now' && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-800 shrink-0 mt-0.5">
                            Buy Now
                          </span>
                        )}

                        <p className="text-base font-bold tabular-nums text-blue-600 dark:text-blue-400 shrink-0 w-24 text-right pt-0.5">
                          ${formatCurrency(bid.bid_price)}
                        </p>

                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 w-20 text-center mt-0.5 ${s.cls}`}>
                          {s.label}
                        </span>

                        <div className="w-full sm:w-48 shrink-0 flex flex-col items-end gap-1.5">
                          {sellerCountered ? (
                            <>
                              <div className="flex items-center gap-2">
                                <button onClick={() => respondToCounter(bid, 'rejected')}
                                  disabled={responding[bid.id]}
                                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-red-300 hover:text-red-600 hover:bg-red-50 dark:hover:border-red-700 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40">
                                  Decline
                                </button>
                                <button onClick={() => respondToCounter(bid, 'accepted')}
                                  disabled={responding[bid.id]}
                                  className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-40">
                                  Accept
                                </button>
                              </div>
                              <div className="flex items-center border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 transition-shadow">
                                <span className="pl-2.5 text-slate-400 text-xs">$</span>
                                <input
                                  type="number"
                                  min="1"
                                  placeholder="Counter"
                                  value={counterAmounts[bid.id] || ''}
                                  onChange={e => setCounterAmounts(a => ({ ...a, [bid.id]: e.target.value }))}
                                  className="w-20 px-1.5 py-1.5 text-xs bg-transparent text-slate-800 dark:text-slate-200 focus:outline-none"
                                />
                                <button
                                  onClick={() => submitCounter(bid)}
                                  disabled={!counterAmounts[bid.id] || responding[bid.id]}
                                  className="px-2 py-1.5 text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 border-l border-slate-200 dark:border-slate-600 transition-colors disabled:opacity-40"
                                >
                                  Counter
                                </button>
                              </div>
                            </>
                          ) : awaitingSeller ? (
                            <span className="text-xs text-slate-400 dark:text-slate-500 italic">Awaiting seller response</span>
                          ) : bid.status === 'accepted' ? (
                            <button onClick={() => navigate('/buyer/pending')} className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline">
                              View in Purchases →
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
